import { useState } from 'react';
import { validateEmail, validatePassword, validatePasswordConfirm } from '../../utils';

/**
 * 로그인 폼 상태 관리를 담당하는 커스텀 훅
 * @param {boolean} isSignup - 회원가입 모드 여부
 * @param {string} email - 이메일 상태
 * @param {function} setEmail - 이메일 설정 함수
 * @param {string} password - 비밀번호 상태
 * @param {function} setPassword - 비밀번호 설정 함수
 * @param {function} handleEmailAuth - 이메일 인증 함수
 * @param {function} onClose - 다이얼로그 닫기 함수
 * @returns {object} - 폼 관련 상태와 함수들
 */
export const useLoginForm = (isSignup, email, setEmail, password, setPassword, handleEmailAuth, onClose, setAuthError = null) => {
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [localError, setLocalError] = useState('');

  /**
   * 이메일 변경 핸들러
   * @param {Event} e - 입력 이벤트
   */
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (!validateEmail(e.target.value)) {
      setEmailError('유효한 이메일 주소를 입력해주세요.');
    } else {
      setEmailError('');
    }
    // 입력 시 로그인 에러 초기화
    if (setAuthError) {
      setAuthError('');
    }
    // 로컬 에러도 초기화
    setLocalError('');
  };

  /**
   * 비밀번호 변경 핸들러
   * @param {Event} e - 입력 이벤트
   */
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    const error = validatePassword(e.target.value, isSignup);
    setPasswordError(error);
    // 입력 시 로그인 에러 초기화
    if (setAuthError) {
      setAuthError('');
    }
    // 로컬 에러도 초기화
    setLocalError('');
  };

  /**
   * 비밀번호 확인 변경 핸들러
   * @param {Event} e - 입력 이벤트
   */
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    const confirmError = validatePasswordConfirm(password, e.target.value);
    if (confirmError) {
      setPasswordError(confirmError);
    } else {
      setPasswordError(validatePassword(password, isSignup));
    }
  };

  /**
   * 이메일 폼 제출 핸들러
   * @param {Event} e - 폼 이벤트
   */
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    // 제출 시 에러 초기화
    setLocalError('');
    
    // 회원가입 시 비밀번호 확인 검증
    if (isSignup && password !== confirmPassword) {
      setPasswordError('비밀번호가 일치하지 않습니다.');
      return;
    }
    
    // 이메일 검증
    if (!validateEmail(email)) {
      setEmailError('유효한 이메일 주소를 입력해주세요.');
      return;
    }
    
    // 비밀번호 검증
    const error = validatePassword(password, isSignup);
    if (error) {
      setPasswordError(error);
      return;
    }
    
    // UX 개선: 회원가입 시 모달을 먼저 닫고 스피닝 로더 표시
    if (isSignup) {
      onClose(); // 모달 즉시 닫기
      const success = await handleEmailAuth();
      // 회원가입 후에는 모달을 다시 열지 않음 (스피닝 로더에서 자동으로 처리됨)
    } else {
      // 로그인 시에는 기존 로직 유지
      const success = await handleEmailAuth();
      if (success) {
        onClose();
      } else {
        setLocalError('잘못된 이메일 또는 비밀번호입니다.');
      }
    }
  };

  /**
   * 폼 상태 완전 초기화
   */
  const resetFormState = () => {
    setConfirmPassword('');
    setPasswordError('');
    setEmailError('');
    setLocalError('');
    // 이메일과 비밀번호는 부모 컴포넌트에서 처리
  };

  return {
    confirmPassword,
    setConfirmPassword,
    passwordError,
    setPasswordError,
    emailError,
    setEmailError,
    localError,
    setLocalError,
    handleEmailChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleEmailSubmit,
    resetFormState
  };
}; 